import React, { FC } from 'react'
import styles from './PolaroidList.module.scss'
import Polaroid, { PolaroidSpecificPageProps } from './Polaroid'
import { SAArtwork } from 'types/Artwork'
import { PolaroidListSkeleton } from './components/PolaroidSkeleton'
import classNames from 'classnames'
import useQA from 'hooks/useQA'
import useSearchProvider, {
  ConstructorAttributeObject,
} from 'components/SA/SearchProvider/hooks/useSearchProvider'

type CheckArtworkToBuildLayoutParams = {
  artwork: SAArtwork
  isValid?: boolean
  validationPropAlias?: string
}

type PolaroidListProps = PolaroidSpecificPageProps & {
  artworks: ReadonlyArray<SAArtwork>
  isLoading?: boolean
  isArtistNameVisible?: boolean
  isPrintsFromVisible?: boolean
  skeletonQuantity?: number
  userID?: number
  isUsersArtworksAccount?: boolean
  customStyle?: string
  isMediumMaterialVisible?: boolean
  isDimensionsVisible?: boolean
  customPriceElement?: (artwork: SAArtwork) => JSX.Element
  customActions?: (artwork: SAArtwork) => JSX.Element
  customPromoCode?: string
  showPrintsFromInPrice?: boolean
  showOriginalPrice?: boolean
  hasPrints?: boolean
  onCheckArtworkToBuildLayout?: (params: CheckArtworkToBuildLayoutParams) => boolean
  onCheckIsPrintForDataLayerEvent?: (artwork: SAArtwork) => boolean
  searchProviderAttributes?: ReadonlyArray<ConstructorAttributeObject>
  imageSuffix?: string
  shouldSendAlgoliaEvents?: boolean
  customArtworkClick?: (ev: React.SyntheticEvent<Record<string, any>>) => void
}

const PolaroidList: FC<PolaroidListProps> = ({
  artworks,
  algoliaIndice,
  algoliaUserToken,
  dataApi,
  dataList,
  dataListId,
  dataListName,
  isLoading = false,
  skeletonQuantity = 6,
  isArtistNameVisible = true,
  isPrintsFromVisible = true,
  userID,
  isMediumMaterialVisible,
  isDimensionsVisible,
  isUsersArtworksAccount,
  customStyle = '',
  customActions,
  customPromoCode,
  showPrintsFromInPrice,
  showOriginalPrice,
  hasPrints,
  onCheckArtworkToBuildLayout,
  onCheckIsPrintForDataLayerEvent,
  searchProviderAttributes,
  imageSuffix,
  shouldSendAlgoliaEvents,
  customArtworkClick,
}) => {
  const { getSearchProviderAttributes } = useSearchProvider()
  const { testId } = useQA()

  if (isLoading) {
    return <PolaroidListSkeleton skeletonQuantity={skeletonQuantity} customStyle={customStyle} />
  }

  if (!artworks?.length) {
    return null
  }

  return (
    <div
      className={classNames(styles.polaroidListContainer, {
        [customStyle]: customStyle !== '',
      })}
      {...testId('polaroid-list-container')}
      {...(!!searchProviderAttributes ? getSearchProviderAttributes(searchProviderAttributes) : {})}
    >
      {artworks.map((artwork, index) => {
        const priority = index === 0
        const lazyLoad = index > 3
        const isImageSrcFromProd = index < 6
        return (
          <Polaroid
            key={artwork.artworkId}
            artwork={artwork}
            algoliaIndice={algoliaIndice}
            algoliaUserToken={algoliaUserToken}
            dataApi={dataApi}
            dataList={dataList}
            dataListId={dataListId}
            dataListName={dataListName}
            isArtistNameVisible={isArtistNameVisible}
            priority={priority}
            lazyLoad={lazyLoad}
            isImageSrcFromProd={isImageSrcFromProd}
            userID={userID}
            isUsersArtworksAccount={isUsersArtworksAccount}
            customActions={!!customActions ? customActions(artwork) : undefined}
            customPromoCode={!!customPromoCode ? customPromoCode : ''}
            shouldSendAlgoliaEvents={shouldSendAlgoliaEvents}
            isPrintsFromVisible={
              !!onCheckArtworkToBuildLayout
                ? onCheckArtworkToBuildLayout({
                    artwork,
                    isValid: isPrintsFromVisible,
                    validationPropAlias: 'isPrintsFromVisible',
                  })
                : isPrintsFromVisible
            }
            isMediumMaterialVisible={
              !!onCheckArtworkToBuildLayout
                ? onCheckArtworkToBuildLayout({
                    artwork,
                    isValid: isMediumMaterialVisible,
                    validationPropAlias: 'isMediumMaterialVisible',
                  })
                : isMediumMaterialVisible
            }
            isDimensionsVisible={
              !!onCheckArtworkToBuildLayout
                ? onCheckArtworkToBuildLayout({
                    artwork,
                    isValid: isDimensionsVisible,
                    validationPropAlias: 'isDimensionsVisible',
                  })
                : isDimensionsVisible
            }
            showPrintsFromInPrice={
              !!onCheckArtworkToBuildLayout
                ? onCheckArtworkToBuildLayout({
                    artwork,
                    isValid: showPrintsFromInPrice,
                    validationPropAlias: 'showPrintsFromInPrice',
                  })
                : showPrintsFromInPrice
            }
            showOriginalPrice={
              !!onCheckArtworkToBuildLayout
                ? onCheckArtworkToBuildLayout({
                    artwork,
                    isValid: showOriginalPrice,
                    validationPropAlias: 'showOriginalPrice',
                  })
                : showOriginalPrice
            }
            hasPrints={
              !!onCheckArtworkToBuildLayout
                ? onCheckArtworkToBuildLayout({
                    artwork,
                    isValid: hasPrints,
                    validationPropAlias: 'hasPrints',
                  })
                : hasPrints
            }
            isPrintForDataLayerEvent={
              !!onCheckIsPrintForDataLayerEvent ? onCheckIsPrintForDataLayerEvent(artwork) : false
            }
            imageSuffix={imageSuffix}
            showPrintsQuantity={hasPrints}
            customArtworkClick={customArtworkClick}
          />
        )
      })}
    </div>
  )
}

export default PolaroidList
